/**
 * What a shopper's filter picks say about them, as affinity tokens.
 *
 * Picking Nike, size M or black in the filter drawer is the plainest statement
 * of taste a buyer makes: more deliberate than opening a listing. Each pick
 * becomes a token `<field>:<value>` named after the Elasticsearch field it
 * filters (`brand:nike`, `colors:black`, `size:m`). The tokens ride in the same
 * affinity vector as tags, and the relevance script matches them against the
 * listing's attribute fields by building the same `<field>:<value>` from each
 * document value. Tags never contain a colon-prefixed field name, so the two
 * kinds cannot collide.
 */

import { ATTRIBUTE_FILTER_FIELDS, type AttributeFilterKey } from './elasticsearch/filter-builders';
import { AFFINITY_ATTRIBUTE_FIELDS } from './elasticsearch/relevance-weights';
import type { FilterState } from './search-filter-state';

/** The document fields the relevance script reads tokens against. */
export const FACET_AFFINITY_FIELDS: readonly string[] = AFFINITY_ATTRIBUTE_FIELDS;

/** One field's value as a token. Lowercased: brand and materials are lowercase-normalised in the index, the rest are lowercase vocabularies. */
export function facetToken(field: string, value: string): string {
  return `${field}:${value.trim().toLowerCase()}`;
}

/** Every attribute pick in a filter state, as tokens. Price, condition and the toggles say nothing about taste. */
export function facetAffinityTokens(filters: FilterState): string[] {
  const tokens: string[] = [];
  for (const [key, field] of Object.entries(ATTRIBUTE_FILTER_FIELDS) as [AttributeFilterKey, string][]) {
    for (const value of filters[key] ?? []) {
      if (typeof value === 'string' && value.trim()) tokens.push(facetToken(field, value));
    }
  }
  return tokens;
}

/**
 * The attribute picks in `next` that were not already applied. Re-applying the
 * same filters, or applying after only a price change, is not a new statement
 * of taste and must not count again.
 */
export function newFacetPicks(next: FilterState, applied: FilterState): FilterState {
  const picks: FilterState = {};
  for (const key of Object.keys(ATTRIBUTE_FILTER_FIELDS) as AttributeFilterKey[]) {
    const before = new Set(applied[key] ?? []);
    const added = (next[key] ?? []).filter(v => !before.has(v));
    if (added.length) picks[key] = added;
  }
  return picks;
}

/** The attribute picks as the `search_filters` map ClickHouse stores: filter name -> comma list. */
export function facetSearchFilters(filters: FilterState): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(ATTRIBUTE_FILTER_FIELDS) as AttributeFilterKey[]) {
    const values = (filters[key] ?? []).filter(v => typeof v === 'string' && v.trim());
    if (values.length) out[key] = values.join(',');
  }
  return out;
}
